import dayjs from 'dayjs';
import type {
    CampaignHostEnterpriseInterval,
    ICampaignHostEnterprisePackage,
    IGrantCampaignHostEnterprisePayload,
} from '../types/global/campaignHostAdmin.types';

export const ENTERPRISE_DURATION_PRESETS = [
    { id: '1m', label: '1 month', months: 1 },
    { id: '3m', label: '3 months', months: 3 },
    { id: '6m', label: '6 months', months: 6 },
    { id: '12m', label: '12 months', months: 12 },
    { id: 'custom', label: 'Custom end date', months: null },
] as const;

export type EnterpriseDurationPresetId = (typeof ENTERPRISE_DURATION_PRESETS)[number]['id'];

export function resolveEnterprisePeriodEnd(presetId: EnterpriseDurationPresetId, customEndIso?: string): string | null {
    if (presetId === 'custom') {
        if (!customEndIso) return null;
        const d = dayjs(customEndIso);
        if (!d.isValid() || !d.isAfter(dayjs())) return null;
        return d.toISOString();
    }
    const preset = ENTERPRISE_DURATION_PRESETS.find(p => p.id === presetId);
    if (!preset?.months) return null;
    return dayjs().add(preset.months, 'month').toISOString();
}

export function resolveEnterpriseInterval(presetId: EnterpriseDurationPresetId): CampaignHostEnterpriseInterval {
    return presetId === '12m' ? 'year' : 'month';
}

export function buildEnterpriseGrantPayload(input: {
    userId: string;
    presetId: EnterpriseDurationPresetId;
    customEndIso?: string;
    replaceExisting: boolean;
    stripeSubscriptionId?: string;
    stripeCustomerId?: string;
    stripePriceId?: string;
    stripeRegion?: 'us' | 'eu';
    listPriceCents?: number;
}): IGrantCampaignHostEnterprisePayload | null {
    const userId = input.userId.trim();
    if (!userId) return null;
    const current_period_end = resolveEnterprisePeriodEnd(input.presetId, input.customEndIso);
    if (!current_period_end) return null;

    return {
        userId,
        interval: resolveEnterpriseInterval(input.presetId),
        current_period_end,
        replace_existing: input.replaceExisting,
        ...(input.listPriceCents != null && { list_price_cents: input.listPriceCents }),
        ...(input.stripeSubscriptionId?.trim() && {
            stripe_subscription_id: input.stripeSubscriptionId.trim(),
        }),
        ...(input.stripeCustomerId?.trim() && { stripe_customer_id: input.stripeCustomerId.trim() }),
        ...(input.stripePriceId?.trim() && { stripe_price_id: input.stripePriceId.trim() }),
        ...(input.stripeRegion && { stripe_region: input.stripeRegion }),
    };
}

export function formatEnterprisePackageLabel(pkg: ICampaignHostEnterprisePackage | null | undefined): string {
    if (!pkg) return 'Campaign Host Enterprise';
    const price =
        typeof pkg.list_price_cents === 'number'
            ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
                  pkg.list_price_cents / 100,
              )
            : null;
    return price ? `${pkg.name} · ${price}/mo` : pkg.name;
}

export function normalizeEnterprisePackage(raw: unknown): ICampaignHostEnterprisePackage | null {
    if (raw == null || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const pkg = (r.package ?? r) as Record<string, unknown>;
    if (!pkg || typeof pkg !== 'object') return null;
    const id = String(pkg._id ?? pkg.id ?? '');
    if (!id && !pkg.slug) return null;
    return {
        _id: id,
        slug: String(pkg.slug ?? 'campaign-host-enterprise'),
        name: String(pkg.name ?? 'Campaign Host Enterprise'),
        description: (pkg.description as string) ?? undefined,
        billing_type: pkg.billing_type === 'lifetime' ? 'lifetime' : 'recurring',
        interval: (pkg.interval as CampaignHostEnterpriseInterval) ?? 'month',
        list_price_cents: Number(pkg.list_price_cents ?? pkg.listPriceCents ?? 2_400_000),
        unlimited_creators: Boolean(pkg.unlimited_creators ?? pkg.unlimitedCreators ?? true),
        unlimited_campaign_creation: Boolean(pkg.unlimited_campaign_creation ?? pkg.unlimitedCampaignCreation ?? true),
        max_creators: (pkg.max_creators ?? pkg.maxCreators ?? null) as number | null,
        features: Array.isArray(pkg.features) ? (pkg.features as string[]) : undefined,
        stripe_product_id: (pkg.stripe_product_id ?? pkg.stripeProductId ?? null) as string | null,
        stripe_price_id: (pkg.stripe_price_id ?? pkg.stripePriceId ?? null) as string | null,
        stripe_eu_product_id: (pkg.stripe_eu_product_id ?? pkg.stripeEuProductId ?? null) as string | null,
        stripe_eu_price_id: (pkg.stripe_eu_price_id ?? pkg.stripeEuPriceId ?? null) as string | null,
        sort_order: Number(pkg.sort_order ?? pkg.sortOrder ?? 100),
    };
}
